/** Reactive app state (Preact signals). Components read .value and re-render. */
import { computed, signal } from "@preact/signals";
import type {
  EmlPreview,
  Envelope,
  FullMessage,
  ListFilter,
  Mailbox,
  RemoteContentDefault,
  SearchOptions,
  UserSettings,
} from "../shared/types";

export type View = "loading" | "login" | "app";
export type MobilePane = "sidebar" | "list" | "reader";

export const view = signal<View>("loading");

/* auth */
export const userEmail = signal("");
export const isAdmin = signal(false);
export const authError = signal<string | null>(null);
export const signingIn = signal(false);
export const defaultDomain = signal("");
/** True when the self-service password-change feature flag is enabled server-side. */
export const passwordChangeEnabled = signal(false);
/**
 * New-password policy bounds, hydrated from the server session so the client
 * never hardcodes the real policy. Values below are pre-hydration placeholders
 * (mirroring the server defaults); the live bounds arrive with SessionInfo.
 */
export const passwordMinLength = signal(12);
export const passwordMaxLength = signal(64);
/**
 * The account's remote-content default, hydrated from SessionInfo. Drives the
 * Reader's "Remote content was blocked" banner (suppressed when `always`) and
 * the Settings Privacy toggle. Pre-hydration placeholder is the safe `block`.
 */
export const remoteContentDefault = signal<RemoteContentDefault>("block");
/** Server-side account settings (display name, signature). */
export const userSettings = signal<UserSettings>({ displayName: "", signature: "", signatureOnReplies: true });

/* mailboxes */
export const mailboxes = signal<Mailbox[]>([]);
export const currentMailbox = signal("INBOX");

/* message list */
export const messages = signal<Envelope[]>([]);
export const messagesTotal = signal(0);
export const page = signal(1);
export const listLoading = signal(false);
export const listError = signal<string | null>(null);
/** True while fetching the next page for infinite scroll (list already has items). */
export const loadingMore = signal(false);
/** Server-side filter applied to the current folder. */
export const listFilter = signal<ListFilter>("all");
/** True while a background refresh (polling, after actions) is running. */
export const refreshing = signal(false);

/* selection / filters */
export const selectedUid = signal<number | null>(null);
/**
 * Mailbox of the open message. Usually the current folder, but a search across
 * all folders can open a message that lives elsewhere. `null` = current folder.
 */
export const selectedMailbox = signal<string | null>(null);
export const selectedUids = signal<Set<number>>(new Set());
/** Anchor for shift-click range selection. */
export const selectionAnchor = signal<number | null>(null);
/** @deprecated kept for compatibility; use `listFilter`. */
export const onlyUnread = computed(() => listFilter.value === "unread");

/* derived */
/** True if more pages exist beyond what's currently loaded (drives infinite scroll). */
export const hasMoreMessages = computed(() => messages.value.length < messagesTotal.value);

export const currentMailboxObj = computed(
  () => mailboxes.value.find((m) => m.path === currentMailbox.value) ?? null,
);

export const visibleMessages = computed(() => messages.value);

/* search */
export const searchQuery = signal("");
export const searchOptions = signal<SearchOptions>({});
export const searchResults = signal<Envelope[] | null>(null);
export const searchLoading = signal(false);
export const searchError = signal<string | null>(null);
export const isSearching = computed(() => searchResults.value !== null);

/** The list the user is currently looking at (search results or folder). */
export const activeList = computed(() => (searchResults.value !== null ? searchResults.value : messages.value));

export const selectedMessage = computed(
  () => activeList.value.find((m) => m.uid === selectedUid.value && (selectedMailbox.value ?? currentMailbox.value) === m.mailbox) ?? null,
);

/* open message (reading pane) */
export const fullMessage = signal<FullMessage | null>(null);
export const messageLoading = signal(false);
export const messageError = signal<string | null>(null);
export const showRemote = signal(false);
/** Other messages of the open message's conversation (oldest first, includes itself). */
export const thread = signal<Envelope[]>([]);

/* nested .eml attachment preview (modal) */
/** Parsed preview of the currently-open `.eml` attachment, or null when closed. */
export const emlPreview = signal<EmlPreview | null>(null);
/** True while the preview request is in flight. */
export const emlPreviewLoading = signal(false);
/** User-facing error message when a preview cannot be shown, else null. */
export const emlPreviewError = signal<string | null>(null);

/** Inline attachment viewer (images / PDF / text). */
export interface AttachmentViewer {
  url: string;
  filename: string;
  contentType: string;
  downloadUrl: string;
}
export const attachmentViewer = signal<AttachmentViewer | null>(null);

/* composer */
export type ComposeMode = "new" | "reply" | "replyAll" | "forward" | "draft";

export interface ComposerDraft {
  mode: ComposeMode;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  /** Threading headers carried from the message being replied to. */
  inReplyTo?: string;
  references?: string[];
  /** Whether the Cc/Bcc row is expanded in the UI. */
  showCc: boolean;
  /** Ids of attached files (contents live in `composerAttachments`). */
  attachmentIds?: string[];
}

export interface ComposerAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  /** base64 content; null while still being read. */
  content: string | null;
}

export const composer = signal<ComposerDraft | null>(null);
export const composerSending = signal(false);
export const composerError = signal<string | null>(null);
export const composerNotice = signal<string | null>(null);
export const composerAttachments = signal<ComposerAttachment[]>([]);
/** Docked window state: normal, collapsed to a title bar, or expanded to a large modal. */
export const composerWindow = signal<"normal" | "minimized" | "maximized">("normal");
/** Bumped every time a composer is (re)opened, so the editor re-initializes its body. */
export const composerSession = signal(0);
/** True while a send is held in the undo window (composer hidden, not yet sent). */
export const composerPendingSend = signal(false);

/* draft persistence */
/** Snapshot of the composer state at the time it was opened / last reset. */
export const composerInitial = signal<ComposerDraft | null>(null);
/** IMAP UID of the most recently saved draft in the Drafts mailbox. */
export const composerDraftUid = signal<number | null>(null);
/** True while an autosave request is in flight. */
export const composerSaving = signal(false);
/** Snapshot of the composer state at the time of the last successful save. */
export const composerLastSaved = signal<ComposerDraft | null>(null);
/** True when the current composer has unsaved changes (deep compare). */
export const composerDirty = computed(() => {
  const cur = composer.value;
  const init = composerInitial.value;
  if (cur === null && init === null) return false;
  if (cur === null || init === null) return true;
  return JSON.stringify(cur) !== JSON.stringify(init);
});
/** True when the confirmation dialog should be shown. */
export const composerShowConfirm = signal(false);

/* folder management */
export const folderEditPath = signal<string | null>(null);
export const folderCreateParent = signal<string | null | undefined>(undefined);
// undefined = closed, null = root level, string = nested under this path
export const folderActionError = signal<string | null>(null);

/* toasts */
export interface Toast {
  id: number;
  message: string;
  tone: "neutral" | "error" | "success";
  action?: { label: string; run: () => void };
  /** ms; 0 = sticky until dismissed. */
  duration: number;
}
export const toasts = signal<Toast[]>([]);

/* confirmation dialog */
export interface ConfirmRequest {
  title: string;
  body?: string;
  confirmLabel: string;
  danger?: boolean;
  resolve: (ok: boolean) => void;
}
export const confirmRequest = signal<ConfirmRequest | null>(null);

/* keyboard / modal */
export const showShortcuts = signal(false);
export const showSettings = signal(false);
export const showCommandPalette = signal(false);
/** Open folder picker for "Move to…": the UIDs being moved, or null when closed. */
export const movePicker = signal<{ uids: number[]; mailbox: string } | null>(null);

/* mobile navigation */
export const mobilePane = signal<MobilePane>("list");
/** Mobile/tablet folder drawer. */
export const drawerOpen = signal(false);
